/** The outline's rows (plan §5): a day ("Day 3 · Galway · 4 stops") and a stop ("10:00
 *  Cliffs of Moher · 3"). Each subscribes to its own record only — a keystroke in another
 *  stop never renders it. In edit mode a row carries its drag handle and its actions (a
 *  day: + stop, move up / down, remove; a stop: move to another day, remove); in view mode
 *  their room is kept, so pressing E moves nothing. A saved row the draft removed stays,
 *  struck through on the changed tint, with Restore, until the save. State is an icon and
 *  words: changed, can't be saved. The chrome is the records outline's (`OutlineRowShell`). */
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, ListSubheader, MenuItem, Tooltip, Typography } from '@mui/material';
import { memo, useState } from 'react';

import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { RestoreButton, StateMarks, stateWords, visuallyHidden } from '@/features/records/fields/RowMarks';
import { useRecordIssues } from '@/features/records/outline/issues';
import { OutlineRowMenu, OutlineRowShell, type OutlineRowProps } from '@/features/records/outline/OutlineRowShell';
import { useOutlineField, useOutlineNode } from '@/features/records/outline/store';

import { useTourRecord } from '../record/TourRecordContext';

import { activityRec, DAY_SLOTS, dayRec, htmlText, itineraryTree, SLOT_ACTIVITIES, slotRec } from './model';
import type { OutlineRow } from './outlineRows';
import { useItinerary } from './store';
import {
  addSlot,
  moveDay,
  moveSlotToDay,
  removeDay,
  removeSlot,
  restoreDay,
  restoreSlot,
} from './structure';
import { useStructure } from './useStructure';

export const DAY_ROW_PX = 52;
export const SLOT_ROW_PX = 36;
export const DAY_ACTIONS_PX = 56;
export const SLOT_ACTIONS_PX = 24;

export interface RowProps extends OutlineRowProps {
  row: OutlineRow;
  /** A day was added from this row: select it / its new stop. */
  onCreated: (kind: 'day' | 'slot', id: number, dayId: number) => void;
  /** Day number by day id (for a stop's "move to"). */
  dayCount: number;
}

// ---------------------------------------------------------------------------
// Day row
// ---------------------------------------------------------------------------

export const OutlineDayRow = memo(function OutlineDayRow({ index, row, active, selected, onSelect, onCreated, dayCount }: RowProps) {
  const { editing, canEdit, issues } = useItinerary();
  const live = editing && canEdit;
  const rec = dayRec(row.id);
  const node = useOutlineNode(rec);
  const recIssues = useRecordIssues(issues, rec);
  const run = useStructure();
  const title = typeof node.value('day.title') === 'string' ? (node.value('day.title') as string).trim() : '';
  const stops = (node.value(DAY_SLOTS) as number[] | undefined)?.length ?? 0;
  const span = node.value('day.day_span');
  const dayIndex = row.kind === 'day' ? row.index : null;
  const number = dayIndex !== null ? dayIndex + 1 : null;
  // A removed day has no number: its title (else "the removed day") names it.
  const name = number !== null ? `Day ${number}${title ? ` · ${title}` : ''}` : title || 'the removed day';
  const invalid = recIssues ? (Object.values(recIssues).flatMap((p) => p.errors)[0] ?? null) : null;
  const changed = editing && node.entries !== undefined && Object.keys(node.entries).some((p) => p !== DAY_SLOTS);
  const label = `${name} · ${stops} ${stops === 1 ? 'stop' : 'stops'}${stateWords({ changed, invalid, removed: row.removed })}`;

  return (
    <OutlineRowShell
      index={index}
      row={row}
      active={active}
      selected={selected}
      onSelect={onSelect}
      live={live}
      level={1}
      name={name}
      label={label}
      height={DAY_ROW_PX}
      actionsWidth={DAY_ACTIONS_PX}
      changed={changed}
      actions={
        row.removed ? (
          <RestoreButton name={name} hint="keep this day and its stops" tabIndex={-1} onRestore={() => run((c) => restoreDay(c, row.id))} />
        ) : (
          <>
            <Tooltip title={`Add a stop to Day ${number}`}>
              <IconButton
                size="small"
                tabIndex={-1}
                aria-label={`Add a stop to Day ${number}`}
                data-add-stop
                onClick={(e) => {
                  e.stopPropagation();
                  const id = run((c) => addSlot(c, row.id));
                  if (typeof id === 'number') onCreated('slot', id, row.id);
                }}
                sx={{ p: 0.25 }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <OutlineRowMenu
              name={name}
              items={(close) => [
                <MenuItem key="up" disabled={dayIndex === 0} onClick={() => { close(); run((c) => moveDay(c, row.id, (dayIndex ?? 0) - 1)); }}>
                  Move up
                </MenuItem>,
                <MenuItem key="down" disabled={dayIndex === dayCount - 1} onClick={() => { close(); run((c) => moveDay(c, row.id, (dayIndex ?? 0) + 1)); }}>
                  Move down
                </MenuItem>,
                <MenuItem key="remove" onClick={() => { close(); run((c) => removeDay(c, row.id)); }} sx={{ color: 'error.main' }}>
                  Remove day{stops > 0 ? ` and its ${stops} ${stops === 1 ? 'stop' : 'stops'}` : ''}
                </MenuItem>,
              ]}
            />
          </>
        )
      }
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'grid' }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {number !== null ? `Day ${number}` : 'Removed'}
          {typeof span === 'number' && span > 1 ? ` · ${span} days` : ''}
          {row.id < 0 ? ' · new' : ''}
        </Typography>
        <Typography variant="body2" noWrap title={title || undefined} sx={{ fontWeight: 600 }}>
          {title || <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Untitled day</Box>}
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            {' '}· {stops} {stops === 1 ? 'stop' : 'stops'}
          </Box>
        </Typography>
      </Box>
      <StateMarks changed={changed} invalid={invalid} />
    </OutlineRowShell>
  );
});

// ---------------------------------------------------------------------------
// Stop row
// ---------------------------------------------------------------------------

/** A stop's name in its row: its title, else its first activity's name. */
function useSlotTitle(slotId: number): string {
  const { names } = useTourRecord();
  const title = useOutlineField(slotRec(slotId), 'slot.title').value;
  const activities = useOutlineField(slotRec(slotId), SLOT_ACTIVITIES).value as number[] | undefined;
  const first = activities?.[0];
  const activity = useOutlineField(activityRec(first ?? 0), 'activity.activity').value;
  if (typeof title === 'string' && title.trim()) return title.trim();
  if (first !== undefined && typeof activity === 'number') return names.activityLabel(activity);
  return '';
}

export const OutlineSlotRow = memo(function OutlineSlotRow({ index, row, active, selected, onSelect }: RowProps) {
  const { editing, canEdit, issues } = useItinerary();
  const live = editing && canEdit;
  const rec = slotRec(row.id);
  const node = useOutlineNode(rec);
  const recIssues = useRecordIssues(issues, rec);
  const run = useStructure();
  const time = typeof node.value('slot.time') === 'string' ? (node.value('slot.time') as string) : '';
  const title = useSlotTitle(row.id);
  const count = (node.value(SLOT_ACTIVITIES) as number[] | undefined)?.length ?? 0;
  const name = `${time || 'No time'} ${title || 'Stop'}`;
  const invalid = recIssues ? (Object.values(recIssues).flatMap((p) => p.errors)[0] ?? null) : null;
  const changed = editing && node.entries !== undefined && Object.keys(node.entries).length > 0;
  const label = `${name} · ${count} ${count === 1 ? 'activity' : 'activities'}${stateWords({ changed, invalid, removed: row.removed })}`;
  const dayId = row.kind === 'slot' ? row.dayId : 0;

  return (
    <OutlineRowShell
      index={index}
      row={row}
      active={active}
      selected={selected}
      onSelect={onSelect}
      live={live}
      level={2}
      name={name}
      label={label}
      height={SLOT_ROW_PX}
      actionsWidth={SLOT_ACTIONS_PX}
      changed={changed}
      actions={
        row.removed ? (
          <RestoreButton name={name} hint="keep this stop" tabIndex={-1} onRestore={() => run((c) => restoreSlot(c, row.id, dayId))} />
        ) : (
          <OutlineRowMenu
            name={name}
            items={(close) => [
              <ListSubheader key="h" sx={{ lineHeight: '32px' }}>
                Move to
              </ListSubheader>,
              <MoveTargetItems key="targets" slotId={row.id} dayId={dayId} close={close} run={run} />,
              <MenuItem key="remove" onClick={() => { close(); run((c) => removeSlot(c, row.id)); }} sx={{ color: 'error.main' }}>
                Remove stop
              </MenuItem>,
            ]}
          />
        )
      }
    >
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', width: 44, flex: 'none', color: time ? 'text.primary' : 'error.main' }}>
        {time || '—'}
      </Typography>
      <Typography variant="body2" noWrap title={title || undefined} sx={{ flex: 1, minWidth: 0 }}>
        {title || <Box component="span" sx={{ color: 'text.secondary' }}>Stop</Box>}
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {' '}· {count}
        </Box>
        <Box component="span" sx={visuallyHidden}>
          {count === 1 ? ' activity' : ' activities'}
        </Box>
      </Typography>
      <StateMarks changed={changed} invalid={invalid} />
    </OutlineRowShell>
  );
});

/** "Move to Day N" items for a stop, from the draft as it stands when the menu opens (its
 *  items mount only while it is open). */
function MoveTargetItems({ slotId, dayId, close, run }: { slotId: number; dayId: number; close: () => void; run: ReturnType<typeof useStructure> }) {
  const { baseline, tourRec, editing } = useItinerary();
  const { store } = useDraftStable();
  const [days] = useState(() => {
    const entries = editing ? store.getState().entries : null;
    return itineraryTree(entries, baseline.paths, tourRec).map((d, index) => {
      const t = entries?.[dayRec(d.id)]?.['day.title']?.to ?? baseline.paths[dayRec(d.id)]?.['day.title'];
      return { id: d.id, index, name: typeof t === 'string' ? htmlText(t) : '' };
    });
  });
  return (
    <>
      {days.map((d) => (
        <MenuItem
          key={d.id}
          disabled={d.id === dayId}
          onClick={() => {
            close();
            run((c) => moveSlotToDay(c, slotId, d.id));
          }}
        >
          Day {d.index + 1}
          {d.name ? ` · ${d.name}` : ''}
          {slotId > 0 && d.id < 0 ? ' — save it first' : ''}
        </MenuItem>
      ))}
    </>
  );
}
